"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, Droplets, ShieldCheck, PackageCheck, TriangleAlert as AlertTriangle, PhoneCall, CircleCheck as CheckCircle2, FileText, CircleQuestionMark as HelpCircle, ArrowRight, Sparkles, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
const commonIssues = [{
  title: "Dripping Spout or Leaking Handle Base",
  cartridge: "Moen 1225 / 1222 / 1255 Cartridges",
  symptom: "Water continues to trickle from spout when turned completely off, or seeps around handle collar.",
  solution: "The internal O-rings or ceramic disc inside the cartridge have degraded due to mineral scale. Replace with an OEM Moen cartridge.",
  urgency: "High - Can waste hundreds of gallons per month and damage vanity counters."
}, {
  title: "Low Water Pressure / Irregular Spray Pattern",
  cartridge: "Aerator & Spray Wand Assembly",
  symptom: "Water flow feels weak, spits sideways, or won't switch cleanly between stream and spray mode.",
  solution: "Disassemble the aerator tip, soak in a vinegar bath to dissolve calcium deposits, and inspect the internal check valve.",
  urgency: "Moderate - Reduces efficiency and stresses the pull-down supply line."
}, {
  title: "Pull-Down Hose Not Retracting Properly",
  cartridge: "Reflex Pulldown Hose & Weight",
  symptom: "Kitchen faucet sprayer wand sags or gets stuck under the sink cabinet when pulled out.",
  solution: "Reposition the counterweight on the hose loop and clear any supply line or garbage disposal obstructions beneath the basin.",
  urgency: "Low - Annoying operational issue that can cause friction wear on the braided nylon hose."
}, {
  title: "Stiff or Difficult-to-Turn Handle",
  cartridge: "Cartridge Stem & Retainer Nut",
  symptom: "Excessive force required to open, close, or adjust water temperature on single-handle faucets.",
  solution: "Limescale buildup around the stem. Shut water off, pull the cartridge with a Moen core puller, clean the brass body, and apply silicone grease.",
  urgency: "Moderate - Continued forcing will snap the plastic stem adapter or brass handle connector."
}];
const productLines = [{
  name: "Moen Arbor & Align Series",
  category: "Kitchen Pull-Down & MotionSense",
  features: "Reflex self-retracting hose, Power Boost stream, optional hands-free smart wave sensors.",
  cartridgeType: "1255 Duralast (Single-handle)",
  bestFor: "High-traffic family kitchens and modern remodeling."
}, {
  name: "Moen Adler & Chateau Series",
  category: "Standard Kitchen & Bath",
  features: "Durable metal lever designs, cost-effective maintenance, classic low-arc spouts.",
  cartridgeType: "1225 Brass / Plastic core",
  bestFor: "Rental properties, utility sinks, and budget-conscious homeowner repairs."
}, {
  name: "Moen Eva, Brantford & Voss",
  category: "Bathroom Lavatory & Tub/Shower",
  features: "Widespread and centerset vanity configurations with matching Posi-Temp shower valves.",
  cartridgeType: "1222 Posi-Temp (Shower) / 1224 (Two-handle)",
  bestFor: "Master bathrooms requiring balanced water pressure and anti-scald protection."
}, {
  name: "Moen Commercial & M-Dura",
  category: "Heavy-Duty & Hospitality",
  features: "Vandal-resistant construction, solid brass waterways, ceramic disc valves rated for constant cycling.",
  cartridgeType: "Commercial 1234 Duralast ceramic",
  bestFor: "Small business restrooms, commercial prep kitchens, and multi-family buildings."
}];
const repairSteps = [{
  step: "01",
  title: "Shut Off Water Supply Lines",
  description: "Close both hot and cold stop valves under the sink. Open faucet handles to relieve remaining line pressure."
}, {
  step: "02",
  title: "Remove Handle and Retainer Clip",
  description: "Pop the decorative index cap, unscrew the handle hex screw, and pull off the handle assembly to access the brass retaining clip."
}, {
  step: "03",
  title: "Extract the Old Moen Cartridge",
  description: "Remove the U-shaped retainer clip with needle-nose pliers. Use a genuine Moen cartridge puller tool to loosen and slide out the core."
}, {
  step: "04",
  title: "Flush Housing and Install New Core",
  description: "Wipe clean the internal valve body to remove grit. Lubricate the new cartridge O-rings with plumber's silicone grease and slide in until seated."
}, {
  step: "05",
  title: "Reinstall Clip, Handle, and Test",
  description: "Re-insert the U-clip fully into its slot, reattach the handle, slowly restore water supply, and check for leaks under pressure."
}];
const faqs = [{
  q: "How do I identify my Moen faucet model number?",
  a: "Look underneath the sink for a small white tag attached to the braided supply lines or pull-down hose. If missing, measure the faucet dimensions and identify whether it uses a 1225 (single handle pull-out), 1222 (shower), 1255 (Duralast), or 1224 (two-handle hot/cold) cartridge."
}, {
  q: "Does Moen provide free replacement parts under warranty?",
  a: "Yes. Moen faucets come with a Limited Lifetime Warranty for the original homeowner against leaks and finish defects. Moen customer support provides replacement cartridges, aerators, and mounting hardware upon verifying purchase."
}, {
  q: "Why is my Moen faucet still leaking after installing a new cartridge?",
  a: "This happens if mineral debris was not flushed out of the brass valve body before inserting the new cartridge, if the O-rings tore during dry insertion, or if the retaining clip was not seated completely into both side grooves."
}, {
  q: "Can I replace a Moen cartridge myself or should I call an emergency plumber?",
  a: "While simple for experienced DIYers, older cartridges often seize tight inside the brass body. Applying excessive wrench torque without the proper puller tool can fracture the copper water lines inside your wall or cabinet."
}];
export default function MoenGuide() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="moenfixtureguide" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-semibold text-xs tracking-wide">
            <Wrench className="size-3.5 text-primary" />
            FIXTURE REPAIR MANUAL
          </Badge>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Moen Faucet & Sink Diagnostic Guide
          </h2>
          <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed">
            Professional troubleshooting, genuine OEM cartridge replacement procedures, and warranty support for Moen kitchen and bathroom fixtures in Jonesboro, GA.
          </p>
        </div>

        {/* Urgent Emergency Alert Banner */}
        <Alert className="mb-12 border-primary/20 bg-secondary/50">
          <AlertTriangle className="size-5 text-primary" />
          <AlertTitle className="text-base font-semibold text-foreground">
            Active under-sink leak or frozen Moen valve?
          </AlertTitle>
          <AlertDescription className="text-sm text-muted-foreground mt-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <span>
              If your fixture is flooding the vanity cabinet or spraying continuously, shut off the main valve and contact our 24/7 plumbing team immediately.
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <Button asChild size="sm" variant="default" className="font-medium">
                <Link to="/faucet-sink-repair/">Schedule Repair</Link>
              </Button>
              <Button asChild size="sm" variant="outline" className="font-medium">
                <Link to="/emergency-plumbing/">24/7 Emergency Service</Link>
              </Button>
            </div>
          </AlertDescription>
        </Alert>

        {/* Tabbed In-Depth Guide */}
        <Tabs defaultValue="issues" className="w-full">
          <div className="flex justify-center mb-8 overflow-x-auto pb-2">
            <TabsList className="h-11 p-1 bg-muted/80">
              <TabsTrigger value="issues" className="px-4 py-2 font-medium text-sm">
                <AlertTriangle className="size-4 mr-2" />
                Common Issues & Fixes
              </TabsTrigger>
              <TabsTrigger value="lines" className="px-4 py-2 font-medium text-sm">
                <Layers className="size-4 mr-2" />
                Product Lines & Models
              </TabsTrigger>
              <TabsTrigger value="repair" className="px-4 py-2 font-medium text-sm">
                <Wrench className="size-4 mr-2" />
                Step-by-Step Cartridge Repair
              </TabsTrigger>
              <TabsTrigger value="warranty" className="px-4 py-2 font-medium text-sm">
                <ShieldCheck className="size-4 mr-2" />
                Warranty & OEM Parts
              </TabsTrigger>
            </TabsList>
          </div>

          {/* TAB 1: Common Issues */}
          <TabsContent value="issues" className="focus-visible:ring-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {commonIssues.map((issue, i) => <div key={issue.title} data-index={i}>
                  <Card className="h-full border border-border shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg font-bold text-foreground">
                          {issue.title}
                        </CardTitle>
                        <Badge variant="outline" className="text-2xs font-mono shrink-0">
                          {issue.cartridge}
                        </Badge>
                      </div>
                      <CardDescription className="text-sm text-muted-foreground pt-1">
                        <strong className="text-foreground">Symptom: </strong>
                        {issue.symptom}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3 pb-4">
                      <div className="rounded-md bg-muted/50 p-3 text-sm">
                        <p className="text-foreground font-medium mb-1 flex items-center gap-1.5">
                          <CheckCircle2 className="size-4 text-primary shrink-0" />
                          Recommended Solution
                        </p>
                        <p className="text-muted-foreground text-xs leading-relaxed">
                          {issue.solution}
                        </p>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        <strong className="text-foreground">Urgency Level: </strong>
                        {issue.urgency}
                      </div>
                    </CardContent>
                    <CardFooter className="pt-0 border-t border-border/40 flex justify-end">
                      <Button asChild variant="link" size="sm" className="px-0 text-primary">
                        <Link to="/faucet-sink-repair/" className="inline-flex items-center gap-1">
                          Request fixture technician <ArrowRight className="size-3.5" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </div>)}
            </div>
          </TabsContent>

          {/* TAB 2: Product Lines */}
          <TabsContent value="lines" className="focus-visible:ring-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {productLines.map((prod, i) => <div key={prod.name} data-index={i}>
                  <Card className="h-full border border-border flex flex-col justify-between shadow-xs">
                    <CardHeader className="pb-3">
                      <Badge variant="secondary" className="w-fit text-2xs mb-2">
                        {prod.category}
                      </Badge>
                      <CardTitle className="text-base font-bold text-foreground">
                        {prod.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm flex-1">
                      <p className="text-muted-foreground text-xs leading-relaxed">
                        {prod.features}
                      </p>
                      <Separator />
                      <div>
                        <span className="text-xs font-semibold text-foreground block mb-0.5">
                          Standard Cartridge:
                        </span>
                        <span className="text-xs text-muted-foreground font-mono bg-muted px-2 py-0.5 rounded">
                          {prod.cartridgeType}
                        </span>
                      </div>
                      <div>
                        <span className="text-xs font-semibold text-foreground block mb-0.5">
                          Ideal Usage:
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {prod.bestFor}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>)}
            </div>
          </TabsContent>

          {/* TAB 3: Step-by-Step Cartridge Repair */}
          <TabsContent value="repair" className="focus-visible:ring-0">
            <Card className="border border-border">
              <CardHeader className="border-b border-border/60 pb-4">
                <CardTitle className="text-xl font-bold text-foreground">
                  Moen One-Handle Cartridge Replacement Procedure
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Follow this standardized five-step sequence when replacing seized or leaking 1225, 1222, and 1255 valves.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {repairSteps.map((st, i) => <div key={st.step} data-index={i} className="relative flex flex-col p-4 rounded-lg bg-card border border-border">
                      <div className="text-2xl font-bold font-mono text-primary/40 mb-2">
                        {st.step}
                      </div>
                      <h4 className="font-semibold text-sm text-foreground mb-1.5">
                        {st.title}
                      </h4>
                      <p className="text-xs text-muted-foreground leading-relaxed flex-1">
                        {st.description}
                      </p>
                    </div>)}
                </div>

                <div className="mt-6 p-4 rounded-lg bg-muted/60 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <PackageCheck className="size-6 text-primary shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        Need genuine Moen puller tools or professional install?
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Our service trucks carry official Moen cartridges and extraction equipment for all residential models.
                      </p>
                    </div>
                  </div>
                  <Button asChild size="sm" variant="default" className="shrink-0 font-medium">
                    <Link to="/faucet-sink-repair/">Book a Plumber</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 4: Warranty & OEM Parts */}
          <TabsContent value="warranty" className="focus-visible:ring-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="border border-border lg:col-span-2">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="size-5 text-primary" />
                    <CardTitle className="text-lg font-bold text-foreground">
                      Moen Lifetime Limited Warranty Overview
                    </CardTitle>
                  </div>
                  <CardDescription className="text-sm text-muted-foreground">
                        Moen warrants to the original consumer purchaser that all faucet components will be leak and drip-free during normal residential use.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-muted-foreground">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="p-3.5 rounded-lg border border-border bg-card">
                      <h5 className="font-semibold text-foreground mb-1 flex items-center gap-1.5 text-xs">
                        <CheckCircle2 className="size-3.5 text-primary" />
                        Covered Under Warranty
                      </h5>
                      <ul className="text-xs space-y-1 list-disc list-inside text-muted-foreground">
                        <li>Defective ceramic/rubber cartridges</li>
                        <li>Spout finish peeling or flaking</li>
                        <li>Internal check valves and diverters</li>
                        <li>Sprayer head leaks during normal use</li>
                      </ul>
                    </div>
                    <div className="p-3.5 rounded-lg border border-border bg-card">
                      <h5 className="font-semibold text-foreground mb-1 flex items-center gap-1.5 text-xs text-destructive">
                        <AlertTriangle className="size-3.5" />
                        Common Warranty Exclusions
                      </h5>
                      <ul className="text-xs space-y-1 list-disc list-inside text-muted-foreground">
                        <li>Damage from harsh abrasive chemical cleaners</li>
                        <li>Freezing burst damage in outdoor/unheated zones</li>
                        <li>Commercial or industrial installations (5-year term)</li>
                        <li>Labor costs incurred by third-party technicians</li>
                      </ul>
                    </div>
                  </div>
                  <p className="text-xs leading-relaxed">
                    To file a warranty claim, call Moen Customer Support at 1-800-BUY-MOEN or provide your model number and photos via their online parts portal. If you need professional installation of warranty parts, our licensed technicians can install them promptly.
                  </p>
                </CardContent>
              </Card>

              <Card className="border border-border bg-muted/30 flex flex-col justify-between">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <FileText className="size-5 text-primary" />
                    <CardTitle className="text-base font-bold text-foreground">
                      Locate Your Moen Model
                    </CardTitle>
                  </div>
                  <CardDescription className="text-xs text-muted-foreground">
                    Having your exact serial number speeds up replacement parts ordering.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 text-xs text-muted-foreground">
                  <div className="p-2.5 rounded bg-background border border-border">
                    <p className="font-semibold text-foreground mb-0.5">1. Check Under-Sink Tags</p>
                    <p>Inspect the cold water inlet hose for a white tag with a 4- or 5-digit series code.</p>
                  </div>
                  <div className="p-2.5 rounded bg-background border border-border">
                    <p className="font-semibold text-foreground mb-0.5">2. Match the Spout Profile</p>
                    <p>Compare your faucet arc height and handle position with Moen catalog listings.</p>
                  </div>
                  <div className="p-2.5 rounded bg-background border border-border">
                    <p className="font-semibold text-foreground mb-0.5">3. Verify Shower Escutcheon</p>
                    <p>Look for the small etched logo and temperature stop indicator on the wall plate.</p>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button asChild variant="outline" size="sm" className="w-full text-xs font-medium">
                    <Link to="/contact/">Help Me Identify My Fixture</Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Frequently Asked Questions Accordion */}
        <div className="mt-16 max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="font-default text-2xl font-bold text-foreground mb-2">
              Moen Fixture Troubleshooting Questions
            </h3>
            <p className="text-sm text-muted-foreground">
              Straightforward answers to the most frequent Moen faucet and shower maintenance questions.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={faq.q} value={`faq-${i}`} data-index={i}>
                <AccordionTrigger className="text-sm md:text-base font-semibold text-foreground hover:no-underline hover:text-primary transition-colors">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {/* Final Action CTA Block */}
        <div className="mt-16 rounded-2xl bg-card border border-border p-8 md:p-10 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="text-xl md:text-2xl font-bold text-foreground">
              Have a stubborn Moen leak in Jonesboro, GA?
            </h3>
            <p className="text-sm text-muted-foreground max-w-xl">
              From frozen cartridge extractions to complete kitchen and bathroom fixture upgrades, our local plumbing experts are ready 24/7.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" variant="default" className="font-semibold shadow-xs">
              <Link to="/faucet-sink-repair/">
                Schedule Moen Repair
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="font-semibold">
              <Link to="/contact/">
                Contact Our Team
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}